// Web Push 订阅辅助。
//
// 流程（PWA 端）：
//   1. 浏览器先要 Notification.requestPermission()
//   2. 拿 ServiceWorker registration → registration.pushManager.subscribe({applicationServerKey: vapidPub})
//   3. 把订阅 JSON POST 到 /api/push/subscribe（HMAC 鉴权）
//   4. 退订时 unsubscribe + DELETE /api/push/unsubscribe
//
// VAPID public key 是 Uncompressed P-256 EC point（65 byte）。浏览器要的是 Uint8Array，
// 所以 base64url → bytes。

import { signRequest } from "../crypto/hmac";
import { tokenStore } from "../storage/tokens";

export interface SubscribeResult {
  ok: boolean;
  endpoint?: string;
  reason?: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function authedFetch(method: string, path: string, body: unknown | null) {
  const ident = await tokenStore.getIdentity();
  if (!ident) throw new Error("no_identity");
  const bodyStr = body == null ? null : JSON.stringify(body);
  const { authHeaderValue } = await signRequest(
    ident.device_id, ident.token, method, path, bodyStr
  );
  const headers: Record<string, string> = { "X-Auth-Ear": authHeaderValue };
  if (bodyStr != null) headers["Content-Type"] = "application/json";
  const url = new URL(path, ident.gw_url).toString();
  return fetch(url, { method, headers, body: bodyStr ?? undefined });
}

/** 拉 server-side VAPID public key（每次订阅前都拉，避免 GW 换 key 时客户端缓存过时） */
async function fetchVapidPublicKey(): Promise<string> {
  const r = await authedFetch("GET", "/api/push/vapid-key", null);
  if (!r.ok) throw new Error(`vapid-key fetch ${r.status}`);
  const j = await r.json();
  if (!j.public_key) throw new Error("vapid-key missing public_key");
  return j.public_key as string;
}

/** 订阅 Web Push */
export async function subscribePush(): Promise<SubscribeResult> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "browser_unsupported" };
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: `permission_${perm}` };

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();

  let subscription = existing;
  if (!subscription) {
    const vapidPub = await fetchVapidPublicKey();
    const keyBytes = urlBase64ToUint8Array(vapidPub);
    // TS5 把 Uint8Array<ArrayBufferLike> 收紧；强制裁回 ArrayBuffer
    const keyBuf = keyBytes.buffer.slice(
      keyBytes.byteOffset,
      keyBytes.byteOffset + keyBytes.byteLength
    ) as ArrayBuffer;
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBuf,
    });
  }

  // 把订阅 JSON 发给 GW
  const json = subscription.toJSON();
  const r = await authedFetch("POST", "/api/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
  });
  if (!r.ok) {
    return { ok: false, reason: `subscribe_${r.status}` };
  }
  return { ok: true, endpoint: json.endpoint };
}

/** 退订 */
export async function unsubscribePush(): Promise<SubscribeResult> {
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no_sw" };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true, reason: "no_subscription" };

  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await authedFetch("POST", "/api/push/unsubscribe", { endpoint });
  } catch (e) {
    // 即便 GW 那边删失败也无妨——它们 Day-6 是内存 map，重启就清
  }
  return { ok: true, endpoint };
}

/** 触发服务端 push test（一条 Bark + 一条 Web Push） */
export async function triggerPushTest(): Promise<void> {
  const r = await authedFetch("POST", "/api/push/test", null);
  if (!r.ok) throw new Error(`push test ${r.status}`);
}

/** 当前订阅状态（同步从 ServiceWorker 拉，不打 server） */
export async function getSubscriptionState(): Promise<{
  permission: NotificationPermission;
  subscribed: boolean;
  endpoint?: string;
}> {
  const permission = "Notification" in window ? Notification.permission : "denied";
  if (!("serviceWorker" in navigator)) return { permission, subscribed: false };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return {
      permission,
      subscribed: !!sub,
      endpoint: sub?.endpoint,
    };
  } catch {
    return { permission, subscribed: false };
  }
}
